import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";

import type { LineaManual } from "@/lib/api";

const money = (n: number, mon: string) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency", currency: mon === "USD" ? "USD" : "MXN",
  }).format(n);

/**
 * Editor de CARGOS y CRÉDITOS MANUALES de una factura (concepto + monto).
 * Se capturan ANTES de guardar: entran al subtotal (antes de IVA) y quedan en
 * la memoria de la factura, el detalle y el PDF. Un crédito se captura con
 * monto positivo y el editor lo manda con signo negativo.
 */
export function ManualesEditor({ lineas, onChange, moneda }: {
  lineas: LineaManual[];
  onChange: (lineas: LineaManual[]) => void;
  moneda: string;
}) {
  const [tipo, setTipo] = useState<"cargo" | "credito">("cargo");
  const [concepto, setConcepto] = useState("");
  const [montoStr, setMontoStr] = useState("");

  const agregar = () => {
    const v = Number(montoStr);
    if (!concepto.trim() || !Number.isFinite(v) || v <= 0) return;
    onChange([...lineas, {
      concepto: concepto.trim(),
      monto: tipo === "cargo" ? Math.round(v * 100) / 100 : -Math.round(v * 100) / 100,
    }]);
    setConcepto("");
    setMontoStr("");
  };

  const total = lineas.reduce((s, l) => s + l.monto, 0);

  return (
    <div className="bg-white border rounded-lg p-4 space-y-2">
      <div className="text-sm font-medium text-slate-700">
        Cargos y créditos manuales
        <span className="ml-2 text-[11px] font-normal text-slate-400">
          entran al subtotal (antes de IVA) al guardar la factura
        </span>
      </div>

      {lineas.length > 0 && (
        <table className="w-full text-sm">
          <tbody>
            {lineas.map((l, i) => (
              <tr key={i} className="border-b last:border-0">
                <td className="py-1 pr-2">
                  <span
                    className={`inline-block text-[11px] font-medium rounded-full px-2 py-0.5 ${
                      l.monto >= 0
                        ? "bg-indigo-100 text-indigo-700"
                        : "bg-emerald-100 text-emerald-700"
                    }`}
                  >
                    {l.monto >= 0 ? "Cargo" : "Crédito"}
                  </span>
                </td>
                <td className="py-1 pr-2">{l.concepto}</td>
                <td className={`py-1 pr-2 text-right tabular-nums ${l.monto < 0 ? "text-emerald-700" : ""}`}>
                  {money(l.monto, moneda)}
                </td>
                <td className="py-1 w-8 text-right">
                  <button
                    type="button"
                    title="Quitar esta línea"
                    onClick={() => onChange(lineas.filter((_, j) => j !== i))}
                    className="text-slate-400 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            <tr>
              <td colSpan={2} className="py-1 pr-2 text-right text-xs text-slate-500">
                Suma de manuales (antes de IVA):
              </td>
              <td className={`py-1 pr-2 text-right tabular-nums font-medium ${total < 0 ? "text-emerald-700" : ""}`}>
                {money(total, moneda)}
              </td>
              <td />
            </tr>
          </tbody>
        </table>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <select
          className="border rounded px-2 py-1.5 text-sm"
          value={tipo}
          onChange={(e) => setTipo(e.target.value as "cargo" | "credito")}
        >
          <option value="cargo">Cargo (+)</option>
          <option value="credito">Crédito (−)</option>
        </select>
        <input
          type="text"
          className="border rounded px-2 py-1.5 text-sm flex-1 min-w-56"
          placeholder="Concepto (p. ej. penalización por desbalance, bonificación acordada…)"
          maxLength={300}
          value={concepto}
          onChange={(e) => setConcepto(e.target.value)}
        />
        <input
          type="number"
          step="0.01"
          min="0"
          className="border rounded px-2 py-1.5 text-sm w-36 tabular-nums"
          placeholder={`Monto (${moneda})`}
          value={montoStr}
          onChange={(e) => setMontoStr(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") agregar();
          }}
        />
        <button
          type="button"
          onClick={agregar}
          disabled={!concepto.trim() || !(Number(montoStr) > 0)}
          className="inline-flex items-center gap-1.5 border text-slate-600 rounded px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" /> Agregar
        </button>
      </div>
    </div>
  );
}
